// frontend/src/features/livestock/TankShotGrid.tsx
// Tank Shots as a grid (design 6.2): newest first under month headings, in the
// size the image size control picks. A tile opens the shot in the lightbox,
// or in select mode (design 7.1) picks it for Compare, labelled Before or After.

import { memo, useMemo } from 'react';
import {
  AspectRatio,
  Badge,
  Center,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Title,
  UnstyledButton,
  type SimpleGridProps,
} from '@mantine/core';
import { IconPhoto } from '@tabler/icons-react';
import type { TankPhoto } from '@/types/tankPhotos';
import type { ImageSize } from './imageSize';
import { groupByMonth, parseServerDate } from './monthGroups';
import { formatTakenAt } from './photoInboxFormat';
import classes from './TankShotGrid.module.css';

export interface TankShotGridProps {
  shots: TankPhoto[];
  size: ImageSize;
  onOpen: (shot: TankPhoto) => void;
  /** A thumbnail failed to load (its signed URL may have expired). */
  onImageError: () => void;
  /** Select mode: a tap picks the shot instead of opening it. */
  selection?: TankShotSelection;
}

/** A picked shot's label: Before or After by date, or Selected while only one is picked. */
export type PickLabel = 'Before' | 'After' | 'Selected';

export interface TankShotSelection {
  labelOf: (id: number) => PickLabel | null;
  onPick: (shot: TankPhoto) => void;
}

const COLS: Record<ImageSize, SimpleGridProps['cols']> = {
  thumbnail: { base: 3, xs: 4, sm: 5, md: 6, lg: 8 },
  medium: { base: 2, sm: 3, md: 4 },
  full: { base: 1, sm: 2 },
};

const entryDate = (shot: TankPhoto): string => shot.entry_date;

/** Small tiles load the 300 px thumb, bigger ones the 800 px display variant. */
function tileUrl(shot: TankPhoto, size: ImageSize): string | null {
  return size === 'thumbnail'
    ? shot.photo_thumb_url ?? shot.photo_display_url
    : shot.photo_display_url ?? shot.photo_thumb_url;
}

const ShotTile = memo(function ShotTile({
  shot,
  size,
  onOpen,
  onImageError,
  pick,
}: {
  shot: TankPhoto;
  size: ImageSize;
  onOpen: (shot: TankPhoto) => void;
  onImageError: () => void;
  /** Select mode: undefined outside it, null for a shot not picked. */
  pick?: PickLabel | null;
}): JSX.Element {
  const url = tileUrl(shot, size);
  const selecting = pick !== undefined;
  return (
    <UnstyledButton
      className={classes.tile}
      onClick={() => onOpen(shot)}
      aria-label={`Tank shot, ${formatTakenAt(shot.entry_date)}${shot.is_cover ? ', cover' : ''}${pick ? `, ${pick}` : ''}`}
      aria-pressed={selecting ? !!pick : undefined}
      data-picked={pick ? true : undefined}
    >
      <AspectRatio ratio={size === 'thumbnail' ? 1 : 4 / 3}>
        {url ? (
          <img
            src={url}
            alt=""
            loading="lazy"
            decoding="async"
            className={classes.thumb}
            onError={onImageError}
          />
        ) : (
          <Center className={classes.thumb}>
            <IconPhoto size={16} color="var(--mantine-color-gray-7)" />
          </Center>
        )}
      </AspectRatio>
      <Group gap={6} mt={4} wrap="nowrap">
        <Text size="xs" lh={1.3} className={classes.date}>
          {parseServerDate(shot.entry_date).format('D MMM')}
        </Text>
        {pick && (
          <Badge size="xs" variant="filled" color="teal">
            {pick}
          </Badge>
        )}
        {shot.is_cover && (
          <Badge size="xs" variant="light" color="teal">
            Cover
          </Badge>
        )}
      </Group>
      {size !== 'thumbnail' && shot.entry_text && (
        <Text size="xs" c="dimmed" lineClamp={1}>
          {shot.entry_text}
        </Text>
      )}
    </UnstyledButton>
  );
});

export function TankShotGrid({
  shots,
  size,
  onOpen,
  onImageError,
  selection,
}: TankShotGridProps): JSX.Element {
  const groups = useMemo(() => groupByMonth(shots, entryDate), [shots]);
  return (
    <Stack gap="lg">
      {groups.map((group) => (
        <section key={group.key} aria-label={group.label}>
          <Title
            order={3}
            fz={{ base: 16, sm: 18 }}
            fw={600}
            mb="xs"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {group.label}
          </Title>
          <SimpleGrid cols={COLS[size]} spacing={{ base: 8, sm: 'sm' }}>
            {group.items.map((shot) => (
              <ShotTile
                key={shot.id}
                shot={shot}
                size={size}
                onOpen={selection ? selection.onPick : onOpen}
                onImageError={onImageError}
                pick={selection ? selection.labelOf(shot.id) : undefined}
              />
            ))}
          </SimpleGrid>
        </section>
      ))}
    </Stack>
  );
}
